import React, { useState, useEffect } from 'react';
import axios from 'axios';
import PositionTabs from './PositionTabs.js';
import CreatePositionModal from './CreatePositionModal.js';
import PositionsGrid from './PositionsGrid.js';
import MatchesModal from './MatchesModal.js';
import { Plus } from 'lucide-react';
import styles from './ProfessorPositions.module.css';

export default function ProfessorPositions() {
  // State management
  const [tab, setTab] = useState('view');
  const [positions, setPositions] = useState([]);
  const [matches, setMatches] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingPosition, setEditingPosition] = useState(null);
  const [selectedPositionTitle, setSelectedPositionTitle] = useState('');
  const [isMatching, setIsMatching] = useState(false);

  // User data
  const userData = JSON.parse(localStorage.getItem('user'));

  // Fetch user's positions
  const fetchPositions = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/positions/mine`, {
        headers: {
          Authorization: `Bearer ${userData.token}`,
        },
      });
      setPositions(response.data);
    } catch (error) {
      console.error('Error fetching positions:', error);
      alert('Failed to fetch positions');
    }
  };

  // Load positions when switching to view tab
  useEffect(() => {
    if (tab === 'view') {
      fetchPositions();
    }
  }, [tab]);

  // Handle position creation success
  const handlePositionCreated = () => {
    setShowCreateModal(false);
    setEditingPosition(null);
    fetchPositions();
  };

  const handleEditClick = (position) => {
    setEditingPosition(position);
    setShowCreateModal(true);
  };

  // Handle position deletion - UPDATED
  const handlePositionDeleted = (deletedPositionId) => {
    // Immediately remove the deleted position from the UI
    setPositions(prevPositions => {
      const updatedPositions = prevPositions.filter(position => position._id !== deletedPositionId);
      return updatedPositions;
    });
  };

  // Handle matching functionality
  const handleMatchClick = async (position) => {
    setSelectedPositionTitle(position.title);
    setShowModal(true);
    setIsMatching(true);
    setMatches([]);

    const extractMatchData = (pos) =>
      (pos.matchedStudents || [])
        .sort((a, b) => (b.final_score || 0) - (a.final_score || 0))
        .map(match => ({
          id: (typeof match.student === 'string' ? match.student : match.student?._id)?.toString(),
          score: match.final_score
        }))
        .filter(m => m.id);

    let matchData = extractMatchData(position);
    let studentIds = matchData.map(m => m.id);

    try {
      if (studentIds.length === 0) {
        await axios.get(`${process.env.REACT_APP_FASTAPI_URL}/match-candidates`, {
          params: { position_id: position._id },
        });

        const { data: updatedPosition } = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/positions/${position._id}`,
          {
            headers: { Authorization: `Bearer ${userData.token}` },
          }
        );

        setPositions(previousPositions =>
          previousPositions.map(pos =>
            pos._id === updatedPosition._id ? updatedPosition : pos
          )
        );

        matchData = extractMatchData(updatedPosition);
        studentIds = matchData.map(m => m.id);
      }

      if (studentIds.length > 0) {
        const token = userData.token || localStorage.getItem('token');
        const { data: matchedUsers } = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/users/batch`, {
          params: { ids: studentIds.join(',') },
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        const matchedUsersWithScores = matchData.map(m => {
          const user = matchedUsers.find(u => (u._id || u.id)?.toString() === m.id);
          return user ? { ...user, matchScore: m.score } : null;
        }).filter(Boolean);

        setMatches(matchedUsersWithScores);
      } else {
        const token = userData.token || localStorage.getItem('token');
        const { data } = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/users`, {
          params: { role: 'student', limit: 12 },
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        setMatches(data.users || []);
      }
    } catch (error) {
      console.error('Matching error:', error);
      alert('❌ Matching failed');
    } finally {
      setIsMatching(false);
    }
  };

  return (
    <div className={styles.professorPositions}>
      <div className={styles.professorPositionsContainer}>
        <div className={styles.dashboardActionsHeader}>
          <h2 className={styles.dashboardTitle}>My Positions</h2>
          <button
            className={styles.primaryActionButton}
            onClick={() => setShowCreateModal(true)}
          >
            <Plus size={20} />
            Create New Position
          </button>
        </div>

        <PositionsGrid
          positions={positions}
          onMatchClick={handleMatchClick}
          onPositionDeleted={handlePositionDeleted}
          onEdit={handleEditClick}
        />

        <CreatePositionModal
          isOpen={showCreateModal}
          onClose={() => {
            setShowCreateModal(false);
            setEditingPosition(null);
          }}
          onPositionCreated={handlePositionCreated}
          userData={userData}
          initialData={editingPosition}
        />

        <MatchesModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          positionTitle={selectedPositionTitle}
          matches={matches}
          isLoading={isMatching}
        />
      </div>
    </div>
  );
}
